import { useTranslation } from "react-i18next";
// src/pages/deposits/NewDeposit.tsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { ArrowLeftIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/solid";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { apiGet, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import VendorEntry from "../VendorEntry";
import { ymdTodayLocal } from "../../utils/dateUtils";
import { A } from "../../styles/appleClasses";
import { AppleDateInput } from "../../components/AppleDateInput";
import { AppleNumberInput } from "../../components/AppleNumberInput";
import MemoSection from "../../components/MemoSection";
import {
  formatClosedThroughDate,
  extractApiDetailOutcome,
  isClosedPeriodError,
  isDateClosed,
} from "../../utils/closedPeriod";
import {
  IDLE_DEPOSIT_CREATE_ATTEMPT,
  DepositCreateAttempt,
  canonicalDepositCreate,
  fingerprintDepositCreate,
  randomUuidKey,
  decideDepositCreateAttempt,
  resolveDepositCreateSuccess,
  resolveDepositCreateDefiniteRejection,
  resolveDepositCreateAmbiguousFailure,
  shouldRestoreAmbiguousAttempt,
  classifyDepositCreateFailure,
} from "../../utils/depositCreateIdempotency";

const RECOGNITION_DEPOSITS_PATH = "/recognition/deposits";

/* ───────────────────────── Types ───────────────────────── */
type Vendor = {
  id: number | string;
  name: string;
  email?: string | null;
  mobile?: string | null;
  contact?: string | null;
  address?: string | null;
};

type Account = {
  id: number | string;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

/* ───────────────────────── Utils ───────────────────────── */
const lower = (s?: string | null) => (s || "").toLowerCase();
const accountDisplayName = (a?: Account | null) =>
  lower(a?.kind) === "credit_card_payable" ? "Credit Card" : a?.name || "";
const byDisplayName = <T extends { name?: string | null }>(a: T, b: T) =>
  (a.name ?? "").localeCompare(b.name ?? "", undefined, {
    sensitivity: "base",
    numeric: true,
  });

const sortPaymentAccounts = (accounts: Account[]) => {
  const cash = accounts
    .filter((a) => lower(a.kind) === "cash")
    .sort(byDisplayName);

  const bank = accounts
    .filter((a) => lower(a.kind) === "bank")
    .sort(byDisplayName);

  const creditCard = accounts
    .filter((a) => lower(a.kind) === "credit_card_payable")
    .sort(byDisplayName);

  return [...cash, ...bank, ...creditCard];
};

type TextEl = HTMLInputElement | HTMLTextAreaElement;

function parseMoneyInput(v: string) {
  const n = parseFloat(
    String(v || "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(n) ? n : NaN;
}

async function fetchBookDate(company_id: string): Promise<string> {
  const candidates = [
    `/book-date?company_id=${company_id}`,
    `/settings/book-date?company_id=${company_id}`,
    `/settings?company_id=${company_id}`,
  ];
  for (const url of candidates) {
    try {
      const res: any = await apiGet<any>(url);
      const d =
        typeof res === "string"
          ? res
          : res?.date || res?.book_date || res?.current_date || res?.today;
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    } catch {}
  }
  return ymdTodayLocal();
}

/* ───────────────────────── Page ───────────────────────── */
export default function NewDeposit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  // Deposit paid to vendor
  const direction: "outgoing" = "outgoing";

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendor, setVendor] = useState<Vendor | null>(null);

  const [paymentAccounts, setPaymentAccounts] = useState<Account[]>([]);
  const [paymentAccount, setPaymentAccount] = useState<Account | null>(null);

  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(ymdTodayLocal());
  const [memo, setMemo] = useState("");

  const [saving, setSaving] = useState(false);
  type DisplayError = string | { key:
    "newDeposit.selectVendorError" | "newDeposit.selectAccountError" |
    "newDeposit.positiveAmount" | "newDeposit.validDate" |
    "newDeposit.invalidCompany" | "newDeposit.editedRetry" |
    "newDeposit.conflict" | "sales.errors.generic" } |
    { key: "sales.errors.closedSubmit"; closedDate: string | null };
  const [error, setError] = useState<DisplayError>("");
  const syntheticVendors = useRef(new WeakSet<Vendor>());
  const syntheticAccounts = useRef(new WeakSet<Account>());
  const vendorLabel = (value: Vendor) => syntheticVendors.current.has(value)
    ? t("newDeposit.vendorFallback", { id: value.id }) : value.name;
  const accountLabel = (value: Account) => syntheticAccounts.current.has(value)
    ? t("newDeposit.accountFallback", { id: value.id })
    : lower(value.kind) === "credit_card_payable"
      ? t("expenses.entry.creditCard") : value.name;
  const apiErrorDisplay = (err: unknown): DisplayError => {
    const outcome = extractApiDetailOutcome(err);
    return outcome.kind === "generic" ? { key: "sales.errors.generic" }
      : outcome.text || "Failed to save deposit.";
  };
  const errorText = typeof error === "string" ? error
    : error.key === "sales.errors.closedSubmit"
      ? t("sales.errors.closedSubmit", { date: error.closedDate
        ? formatClosedThroughDate(error.closedDate) : t("sales.errors.closedDate") })
      : t(error.key);

  const [closedThroughDate, setClosedThroughDate] = useState<string | null>(
    null,
  );
  const [showNewVendor, setShowNewVendor] = useState(false);

  // Deposit-create idempotency attempt lifecycle (see
  // utils/depositCreateIdempotency.ts). A ref, not state: must be stable
  // across renders, must not itself trigger a re-render, and must be
  // readable synchronously by the in-flight guard below. NewDeposit
  // remounts fresh every time the user navigates to /deposits/new, so a
  // single attempt slot per mounted instance is sufficient -- no
  // resource-scoped attempt map is needed (contrast with
  // FixedAssetDetail.tsx, which is mounted once for the route and reused
  // across different asset_id values).
  const depositCreateAttemptRef = useRef<DepositCreateAttempt>(
    IDLE_DEPOSIT_CREATE_ATTEMPT,
  );
  // Purely synchronous double-click/repeat-tap guard -- independent of the
  // attempt state machine above. Mirrors FixedAssetDetail.tsx's
  // fixedAssetPaymentInFlightRef.
  const depositCreateInFlightRef = useRef(false);

  const bodyCls = (A as any).body || "text-[13px] font-normal text-gray-700";
  const inputBase =
    (A as any).input ||
    "w-full h-10 rounded-xl px-4 border border-gray-200 bg-white text-[13px] font-normal text-gray-700 " +
      "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition shadow-none appearance-none";
  const iconBtn =
    (A as any).iconBtn ||
    "h-10 w-10 inline-flex items-center justify-center rounded-xl hover:bg-gray-100 " +
      "focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const labelCls = "block text-xs text-gray-500 mb-1";
  // Button sizing (match detail pages)
  const btnSmBase =
    "h-10 px-4 py-0 rounded-xl text-[13px] font-medium shadow-sm " +
    "inline-flex items-center justify-center leading-none";
  const btnSmPrimary = `${btnSmBase} min-w-[120px]`;
  const btnSmSecondary =
    `${btnSmBase} min-w-[120px] ` +
    "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-200";

  // Date: ?date= → server book-date → today
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!company_id) return;

      const qs = new URLSearchParams(location.search);
      const d = qs.get("date");
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        if (mounted) setDate(d);
        return;
      }

      const srv = await fetchBookDate(company_id);
      if (mounted) setDate(srv);
    })();

    return () => {
      mounted = false;
    };
  }, [company_id, location.search]);

  // Load vendors
  useEffect(() => {
    if (!company_id) return;
    apiGet<Vendor[]>(`/vendors?company_id=${company_id}`)
      .then((xs) => {
        const list = (xs || []).sort((a, b) => a.name.localeCompare(b.name));
        setVendors(list);
        setVendor(null);
      })
      .catch(() => {
        setVendors([]);
        setVendor(null);
      });
  }, [company_id]);

  // Load payment sources — cash/bank plus system Credit Card Payable.
  useEffect(() => {
    if (!company_id) return;
    apiGet<Account[]>(`/accounts?company_id=${company_id}`)
      .then((xs) => {
        const allowed = new Set(["cash", "bank"]);
        const excluded = new Set(["cash_system", "bank_system"]);
        const banks = sortPaymentAccounts(
          (xs || []).filter((a) => {
            const type = lower(a.type);
            const kind = lower(a.kind);
            if (type === "liability" && kind === "credit_card_payable")
              return true;
            if (type !== "asset") return false;
            if (a.is_system) return false;
            if (excluded.has(kind)) return false;
            return allowed.has(kind);
          }),
        );

        setPaymentAccounts(banks);
        setPaymentAccount((prev) => {
          if (prev && banks.find((b) => String(b.id) === String(prev.id)))
            return prev;
          return banks[0] || null;
        });
      })
      .catch(() => {
        setPaymentAccounts([]);
        setPaymentAccount(null);
      });
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  const isClosedDate = useMemo(() => {
    return isDateClosed(date, closedThroughDate);
  }, [date, closedThroughDate]);

  const onAmountBlur = () => {
    setAmount(amount.trim());
  };

  // Force the form fields back to EXACTLY what was submitted under an
  // ambiguous (still-unresolved) attempt -- used both when a mismatched
  // edited retry is blocked, and right after an ambiguous failure is
  // detected. Necessary because, unlike FixedAssetDetail's payment dialog,
  // this page's inputs are never disabled while a request is in flight, so
  // the user could have kept typing during the request; restoring here
  // guarantees no such in-flight edit silently survives (see
  // utils/depositCreateIdempotency.ts's shouldRestoreAmbiguousAttempt).
  const restoreSubmittedPayload = (attempt: DepositCreateAttempt) => {
    if (!shouldRestoreAmbiguousAttempt(attempt) || !attempt.submittedPayload) {
      return;
    }
    const payload = attempt.submittedPayload;

    const matchedVendor =
      vendors.find((v) => String(v.id) === String(payload.party_id)) || {
        id: payload.party_id,
        name: `Vendor #${payload.party_id}`,
      };
    if (!vendors.includes(matchedVendor)) syntheticVendors.current.add(matchedVendor);
    setVendor(matchedVendor);

    const matchedAccount =
      paymentAccounts.find(
        (a) => String(a.id) === String(payload.bank_account_id),
      ) || { id: payload.bank_account_id, name: `Account #${payload.bank_account_id}` };
    if (!paymentAccounts.includes(matchedAccount)) syntheticAccounts.current.add(matchedAccount);
    setPaymentAccount(matchedAccount);

    setAmount(payload.amount);
    setDate(payload.date);
    setMemo(payload.memo || "");
  };

  const submit = async () => {
    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `saving` state alone can't catch this
    // since it only updates on the next render. Mirrors
    // FixedAssetDetail.tsx's savePayment. Independent of the attempt state
    // machine below.
    if (depositCreateInFlightRef.current) return;

    setError("");
    const amt = parseMoneyInput(amount);

    if (!vendor) return setError({ key: "newDeposit.selectVendorError" });
    if (!paymentAccount) return setError({ key: "newDeposit.selectAccountError" });
    if (isNaN(amt) || amt <= 0)
      return setError({ key: "newDeposit.positiveAmount" });
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date))
      return setError({ key: "newDeposit.validDate" });
    if (!company_id) return setError({ key: "newDeposit.invalidCompany" });

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether this is a first attempt (mint a new
    // key), an unchanged retry of a still-ambiguous attempt (reuse its
    // key), or an edited retry of one (block, no network call, restore the
    // original submitted values).
    const canonicalPayload = canonicalDepositCreate(
      direction,
      vendor.id,
      paymentAccount.id,
      amt,
      date,
      memo || "",
    );
    const fingerprint = fingerprintDepositCreate(canonicalPayload);
    const decision = decideDepositCreateAttempt(
      depositCreateAttemptRef.current,
      canonicalPayload,
      fingerprint,
      randomUuidKey,
    );

    if (decision.kind === "blocked-mismatch") {
      // The previous attempt under this key may already have been
      // recorded -- never silently mint a new key or retry with edited
      // details under the same one. Restore the exact values that were
      // actually submitted so the next click safely reuses the key.
      restoreSubmittedPayload(depositCreateAttemptRef.current);
      setError({ key: "newDeposit.editedRetry" });
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- depositCreateInFlightRef.current
      // already returns above before this point. Kept for correctness of
      // the pure decision function itself.
      return;
    }

    depositCreateAttemptRef.current = decision.attempt;
    depositCreateInFlightRef.current = true;

    setSaving(true);
    try {
      // Send the SAME canonicalPayload object used to build the
      // fingerprint above -- never a separately reconstructed copy --
      // plus idempotency_key.
      await apiPost("/deposits", {
        ...canonicalPayload,
        idempotency_key: decision.idempotencyKey,
      });

      // Confirmed success (create or replay -- both return the same
      // response shape): clear the attempt so the next deposit gets a
      // fresh key instead of leaking this one into it.
      depositCreateAttemptRef.current = resolveDepositCreateSuccess();

      navigate(companyRoute(company_id, RECOGNITION_DEPOSITS_PATH));
    } catch (e: any) {
      const failureClass = classifyDepositCreateFailure(e, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises before
        // any claim/business write commits (same savepoint as every other
        // create()-time HTTPException), so nothing was persisted. Clear
        // the attempt so a corrected date can retry under a new key.
        depositCreateAttemptRef.current = resolveDepositCreateDefiniteRejection();
        setError({ key: "sales.errors.closedSubmit", closedDate: closedThroughDate });
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // Definite: this key is permanently bound to a different,
        // already-completed request. Retrying under it can never succeed
        // for this payload -- clear it so the next attempt mints a fresh
        // one.
        depositCreateAttemptRef.current = resolveDepositCreateDefiniteRejection();
        setError({ key: "newDeposit.conflict" });
        return;
      }

      if (failureClass === "definite-rejection") {
        // HTTP 400/404 business or request validation failure -- nothing
        // persisted. Clear the attempt so the user can edit and retry
        // under a new key.
        depositCreateAttemptRef.current = resolveDepositCreateDefiniteRejection();
        setError(apiErrorDisplay(e));
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload so an unchanged retry reuses the same key
      // and safely replays instead of risking a duplicate, then restore
      // the form to exactly what was submitted (overwriting any edit made
      // while the request was in flight -- see restoreSubmittedPayload).
      depositCreateAttemptRef.current = resolveDepositCreateAmbiguousFailure(
        depositCreateAttemptRef.current,
      );
      restoreSubmittedPayload(depositCreateAttemptRef.current);
      setError(apiErrorDisplay(e));
    } finally {
      depositCreateInFlightRef.current = false;
      setSaving(false);
    }
  };

  const paymentPlaceholder = paymentAccounts.length
    ? t("newDeposit.selectSource")
    : t("newDeposit.noSources");

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center justify-start px-2 py-6 ${bodyCls}`}
    >
      {/* Header (sticky + px-2/mb-2 tokens) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center gap-2 border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
          title={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className="text-[15px] font-semibold text-gray-700 text-center">
            {t("newDeposit.title")}
          </h2>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate(companyRoute(company_id || "", RECOGNITION_DEPOSITS_PATH))
          }
          className={iconBtn}
          aria-label={t("directory.common.close")}
          title={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Card */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm max-w-3xl w-full p-5 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vendor with inlay create */}
          <div className="md:col-span-2">
            <label className={labelCls}>{t("directory.vendors.singular")}</label>

            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <AppleComboBox<Vendor>
                  options={vendors}
                  value={vendor}
                  onChange={setVendor}
                  getOptionLabel={vendorLabel}
                  getOptionSearchText={(opt) => opt.name}
                  preserveQueryOnDisplayChange
                  emptyText={t("sales.shared.noResults")}
                  toggleOptionsLabel={t("sales.shared.toggleOptions")}
                  placeholder={
                    vendors.length ? t("purchases.entry.selectVendor") : t("expenses.entry.noVendors")
                  }
                />
              </div>

              <button
                type="button"
                title={t("directory.vendors.add")}
                className={iconBtn}
                onClick={() => setShowNewVendor(true)}
                aria-label={t("directory.vendors.addAria")}
              >
                <PlusIcon className="w-5 h-5 text-appleblue" />
              </button>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className={labelCls}>{t("sales.common.date")}</label>

            <AppleDateInput
              valueYmd={date}
              onChangeYmd={setDate}
              ariaLabel={t("newDeposit.dateAria")}
              openCalendarLabel={t("sales.shared.openCalendar")}
              inputClassName={`${inputBase} tabular-nums appearance-none`}
              placeholder="MM/DD/YYYY"
            />
            {isClosedDate && (
              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t("sales.errors.closedWarning")}
              </div>
            )}
          </div>

          {/* Payment Source */}
          <div>
            <label className={labelCls}>{t("expenses.entry.paymentSource")}</label>
            <AppleComboBox<Account>
              options={paymentAccounts}
              value={paymentAccount}
              onChange={setPaymentAccount}
              getOptionLabel={accountLabel}
              getOptionSearchText={accountDisplayName}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
              placeholder={paymentPlaceholder}
            />
          </div>

          {/* Amount */}
          <div>
            <label className={labelCls}>{t("sales.common.amount")}</label>
            <AppleNumberInput
              className={`text-right ${A.num}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={onAmountBlur}
              placeholder="0.00"
              required
              autoComplete="off"
            />
          </div>

          {/* Memo */}
          <div className="md:col-span-2">
            <MemoSection
              label={t("sales.common.memo")}
              placeholder={t("sales.common.memoPlaceholder")}
              mode="edit"
              value={memo}
              onChange={setMemo}
              className="mt-4 mb-8"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="secondary"
            className={btnSmSecondary}
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            {t("directory.common.cancel")}
          </Button>
          <Button
            variant="primary"
            className={btnSmPrimary}
            onClick={submit}
            disabled={
              saving ||
              !company_id ||
              !vendor ||
              !paymentAccount ||
              !amount ||
              isNaN(parseMoneyInput(amount)) ||
              parseMoneyInput(amount) <= 0 ||
              !date
            }
          >
            {saving ? t("directory.common.savingEllipsis") : t("sales.common.enter")}
          </Button>
        </div>

        {/* Error (reserve space for layout stability) */}
        <div className="mt-2" style={{ minHeight: 24 }}>
          {error ? (
            <div className="text-red-500 text-[13px] font-medium">{errorText}</div>
          ) : null}
        </div>
      </div>

      {/* Inlay via shared VendorEntry */}
      {showNewVendor && company_id ? (
        <VendorEntry
          mode="inlay"
          variant="full"
          companyId={company_id}
          title={t("directory.vendors.new")}
          onCreated={(v) => {
            setVendors((prev) => {
              const exists = prev.find((x) => String(x.id) === String(v.id));
              const list = exists ? prev : [v, ...prev];
              return list.sort((a, b) => a.name.localeCompare(b.name));
            });
            setVendor(v);
          }}
          onClose={() => setShowNewVendor(false)}
        />
      ) : null}
    </div>
  );
}
